import * as THREE from 'three';

// Bright "Fortnite-ish" match environment (D34): saturated palette, mowed-stripe pitch, outer
// ground, ads-board ring (the visible wall), stepped stands + a one-draw-call instanced crowd.
// Presentation-only; built once per scene from the same SceneConfig the pitch uses.

export interface EnvironmentConfig {
  pitchLength: number;
  pitchWidth: number;
}

const SKY = 0x87c5f0; // bright daytime blue
// (mowed-stripe greens live inline in stripeTexture — canvas fillStyle strings)
const OUTER_GROUND = 0x2f7a47;
const BOARD_COLORS = [0xffffff, 0x3a86ff, 0xffd166, 0xef476f, 0x06d6a0];
const STAND_COLOR = 0x33406b;
const CROWD_PALETTE = [0xffd9b3, 0xf2a766, 0x8c5a3a, 0x5b3a29, 0x3a86ff, 0xef476f, 0xffd166, 0x06d6a0, 0xffffff];

/** Deterministic tiny PRNG (mulberry32) — crowd placement must be identical on every client. */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Two-tone mowed stripes along the pitch length (procedural canvas — zero assets). */
function stripeTexture(): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = 64;
  cv.height = 8;
  const g = cv.getContext('2d')!;
  g.fillStyle = '#4ecb71';
  g.fillRect(0, 0, 32, 8);
  g.fillStyle = '#43bd64';
  g.fillRect(32, 0, 32, 8);
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function buildEnvironment(scene: THREE.Scene, cfg: EnvironmentConfig): void {
  const { pitchLength: L, pitchWidth: W } = cfg;

  // ── sky + lights (bright, warm, saturated) ──
  scene.background = new THREE.Color(SKY);
  scene.fog = new THREE.Fog(SKY, 90, 260); // soft aerial depth; the pitch stays crisp
  scene.add(new THREE.HemisphereLight(0xdff1ff, 0x3f7a44, 1.25));
  const sun = new THREE.DirectionalLight(0xfff3d8, 1.6);
  sun.position.set(14, 26, 8);
  scene.add(sun);

  // ── outer ground (extends far past the pitch) ──
  const outer = new THREE.Mesh(new THREE.PlaneGeometry(L * 4, W * 4), new THREE.MeshStandardMaterial({ color: OUTER_GROUND }));
  outer.rotation.x = -Math.PI / 2;
  outer.position.y = -0.02;
  scene.add(outer);

  // ── striped pitch ──
  const stripes = stripeTexture();
  stripes.repeat.set(L / 8, 1); // 8 m per light+dark pair ⇒ 4 m mowing bands
  const pitch = new THREE.Mesh(new THREE.PlaneGeometry(L, W), new THREE.MeshStandardMaterial({ map: stripes }));
  pitch.rotation.x = -Math.PI / 2;
  scene.add(pitch);

  // ── ads-board ring (the visible "wall" at the arena margin) ──
  const margin = 1.5;
  const bh = 0.9; // board height
  const boardGeoX = new THREE.BoxGeometry(L + 2 * margin + 0.4, bh, 0.2);
  const boardGeoZ = new THREE.BoxGeometry(0.2, bh, W + 2 * margin + 0.4);
  const sides: [THREE.BoxGeometry, number, number, number][] = [
    [boardGeoX, 0, W / 2 + margin, 0],
    [boardGeoX, 0, -W / 2 - margin, Math.PI],
    [boardGeoZ, L / 2 + margin, 0, 0],
    [boardGeoZ, -L / 2 - margin, 0, 0],
  ];
  sides.forEach(([geo, x, z], sideIdx) => {
    // segment the long sides into colored blocks
    const segs = sideIdx < 2 ? 6 : 3;
    const segGeo = sideIdx < 2 ? new THREE.BoxGeometry((L + 2 * margin) / segs, bh, 0.2) : new THREE.BoxGeometry(0.2, bh, (W + 2 * margin) / segs);
    for (let i = 0; i < segs; i++) {
      const m = new THREE.Mesh(segGeo, new THREE.MeshStandardMaterial({ color: BOARD_COLORS[(sideIdx * 2 + i) % BOARD_COLORS.length]! }));
      if (sideIdx < 2) m.position.set(-(L + 2 * margin) / 2 + (i + 0.5) * ((L + 2 * margin) / segs), bh / 2, z);
      else m.position.set(x, bh / 2, -(W + 2 * margin) / 2 + (i + 0.5) * ((W + 2 * margin) / segs));
      scene.add(m);
    }
    void geo;
  });

  // ── stands + crowd: 3 stepped tiers on all four sides (unit cubes, scaled) ──
  const rand = lcg(0xfaf1);
  const crowdGeo = new THREE.BoxGeometry(0.34, 0.5, 0.3);
  const crowdMat = new THREE.MeshStandardMaterial({ color: 0xffffff });
  const spots: { x: number; y: number; z: number; c: number }[] = [];
  const tierGeo = new THREE.BoxGeometry(1, 1, 1);
  const tierMat = new THREE.MeshStandardMaterial({ color: STAND_COLOR });
  const buildSide = (dirX: number, dirZ: number): void => {
    const span = (dirX !== 0 ? W : L) + 2 * margin; // length of this side's stand
    const base = (dirX !== 0 ? L : W) / 2 + margin + 1.6; // distance from centre to the front of tier 0
    for (let tier = 0; tier < 3; tier++) {
      const off = tier * 2.1;
      const h = 0.8 + tier * 1.1; // each tier steps up and back
      const tierMesh = new THREE.Mesh(tierGeo, tierMat);
      tierMesh.scale.set(dirX !== 0 ? 2.2 : span, h, dirZ !== 0 ? 2.2 : span);
      tierMesh.position.set(dirX * (base + off), h / 2 - 0.02, dirZ * (base + off));
      scene.add(tierMesh);
      // crowd dots scattered on this tier
      const n = Math.floor(span * 1.6);
      for (let i = 0; i < n; i++) {
        const along = (rand() - 0.5) * span;
        const depth = (rand() - 0.5) * 1.4;
        spots.push({
          x: dirX !== 0 ? dirX * (base + off) + depth : along,
          y: h + 0.3,
          z: dirZ !== 0 ? dirZ * (base + off) + depth : along,
          c: Math.floor(rand() * CROWD_PALETTE.length),
        });
      }
    }
  };
  buildSide(1, 0);
  buildSide(-1, 0);
  buildSide(0, 1);
  buildSide(0, -1);
  const crowd = new THREE.InstancedMesh(crowdGeo, crowdMat, spots.length);
  const m4 = new THREE.Matrix4();
  const col = new THREE.Color();
  spots.forEach((s, i) => {
    m4.makeTranslation(s.x, s.y, s.z);
    crowd.setMatrixAt(i, m4);
    crowd.setColorAt(i, col.setHex(CROWD_PALETTE[s.c]!));
  });
  crowd.instanceMatrix.needsUpdate = true;
  if (crowd.instanceColor) crowd.instanceColor.needsUpdate = true;
  scene.add(crowd);
}
